import { Button, Dropdown, Flex, Spin, Tag, Tooltip, Typography } from 'antd';
import { Clip } from '../ManualClipsList';
import { Styles } from './styles';
import {
  ClockCircleOutlined,
  CommentOutlined,
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { formatDate } from '../../../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { coreService } from '../../../../services/core/core-service';
import { useClipEventStore } from '../../store/clip.store';
import { useToast } from '../../../../hooks/useToast';

const { CardContainer, CardContent } = Styles;

type Props = {
  clip: Clip;
  showOwner?: boolean;
  showDeleteButton: boolean;
};

export const ManualClipCard: React.FC<Props> = ({ clip, showOwner = true, showDeleteButton }) => {
  const navigate = useNavigate();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { success, error } = useToast();
  const { emit } = useClipEventStore();

  useEffect(() => {
    if (clip.status !== 'processing') {
      return;
    }

    const checkStatus = async () => {
      const status = await coreService.checkClipStatus(clip.id);

      if (status !== 'ready') {
        return;
      }

      clearInterval(interval);
      emit('refreshClips');
    };

    const interval = setInterval(checkStatus, 15000);

    return () => clearInterval(interval);
  }, [clip.id, clip.status]);

  const duration = useMemo(() => {
    if (clip.duration < 60) {
      return `${clip.duration}s`;
    }

    const mins = Math.floor(clip.duration / 60);
    const secs = clip.duration % 60;

    return `${mins}:${secs.toString().padStart(2, '0')}m`;
  }, [clip.duration]);

  const handleCardClick = () => {
    if (isDropdownOpen) {
      return;
    }

    if (clip.status !== 'ready') {
      return;
    }

    navigate(`/clip/${clip.id}`);
  };

  return (
    <Spin spinning={isLoading}>
      <CardContainer
        onClick={handleCardClick}
        style={{ cursor: clip.status !== 'ready' ? 'not-allowed' : 'cursor' }}
      >
        <div style={{ position: 'relative' }}>
          {!clip.thumbnail && (
            <div
              style={{
                background: '#f0f0f0',
                width: '100%',
                height: '130px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Spin size="small" />
            </div>
          )}
          {clip.thumbnail && (
            <img
              src={clip.thumbnail}
              style={{ width: '100%', height: '130px' }}
              alt={clip.title}
              loading="lazy"
            />
          )}
          <Flex
            justify="end"
            gap={4}
            style={{ position: 'absolute', bottom: 6, left: 0, right: 0, padding: 8 }}
          >
            <Tooltip title={'Comments'}>
              <Tag icon={<CommentOutlined />} style={{ margin: 0 }} color="magenta">
                {''}
                {clip.comments}
              </Tag>
            </Tooltip>
            <Tooltip title="Views">
              <Tag icon={<EyeOutlined />} style={{ margin: 0 }} color="orange">
                {''}
                {clip.views}
              </Tag>
            </Tooltip>
            <Tooltip title="Duration">
              <Tag icon={<ClockCircleOutlined />} style={{ margin: 0 }} color="blue">
                {duration}
              </Tag>
            </Tooltip>
          </Flex>

          {showDeleteButton && (
            <div style={{ position: 'absolute', right: 4, top: 4 }}>
              <Dropdown
                trigger={['click', 'hover']}
                onOpenChange={setIsDropdownOpen}
                placement="bottomRight"
                disabled={clip.status !== 'ready'}
                menu={{
                  items: [
                    {
                      key: 'delete',
                      label: 'Delete',
                      danger: true,
                      icon: <DeleteOutlined />,
                      onClick: async () => {
                        try {
                          setIsLoading(true);
                          await coreService.deleteClip(clip.id);
                          success('Clip deleted successfully');
                          emit('refreshClips');
                        } catch (err) {
                          error('Failed to delete clip');
                        } finally {
                          setIsLoading(false);
                        }
                      },
                    },
                  ],
                }}
              >
                <Button icon={<MoreOutlined />} size="small" />
              </Dropdown>
            </div>
          )}
        </div>
        <CardContent>
          <Typography.Text
            style={{
              color: '#000',
              margin: 0,
              fontSize: 16,
              lineHeight: '14px',
              fontWeight: 600,
              display: 'block',
            }}
          >
            {clip.title}
          </Typography.Text>
          <Typography.Text
            style={{
              margin: 0,
              display: 'block',
              lineHeight: '12px',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              color: '#666',
            }}
          >
            {clip.meetingTitle}
          </Typography.Text>
          {showOwner && (
            <Typography.Text
              style={{ margin: 0, display: 'block', lineHeight: '12px', color: '#444' }}
            >
              <UserOutlined style={{ color: '#444' }} /> Created by {clip.owner}
            </Typography.Text>
          )}
          <Typography.Text
            style={{
              margin: 0,
              display: 'block',
              lineHeight: '12px',
              color: 'grey',
              marginTop: '4px',
            }}
          >
            {formatDate(clip.createdAt)}
          </Typography.Text>
        </CardContent>
      </CardContainer>
    </Spin>
  );
};
